import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import secondBackground from '../../assets/common/2nd_background.png';
import './Kyc.css';

// Determine API base URL (keep logic consistent with AuthContext and other pages)
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

const DOCUMENT_TYPE = { value: 'ID_CARD' };
// Keep Sumsub integration code, but default to OCR-based KYC.
// Flip this to true if you want to re-enable Sumsub in the future.
const USE_SUMSUB_KYC = false;

export default function Kyc() {
  const { currentUser, currentRole, userRoles, loading: authLoading, isApproved, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();

  const [error, setError] = useState('');
  const [applicantId, setApplicantId] = useState('');
  const [checking, setChecking] = useState(false);
  const [kycCompleted, setKycCompleted] = useState(false);
  const [statusSnapshot, setStatusSnapshot] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [creatingApplicant, setCreatingApplicant] = useState(false);
  const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [checkingTerms, setCheckingTerms] = useState(false);

  const fileInputRefs = useRef({});

  const isBusy = authLoading || checkingTerms || creatingApplicant || uploading || checking;
  const busyMessage = (() => {
    if (uploading) {
      return USE_SUMSUB_KYC ? t('kyc.uploadingToSumsub') : t('kyc.uploadingForVerification');
    }
    if (checking) return t('kyc.checkingStatus');
    if (checkingTerms) return t('terms.checking') || t('common.loading');
    if (creatingApplicant) return t('kyc.creatingProfileMessage');
    return t('common.loading');
  })();

  const formatAttemptsRemaining = (count) => {
    if (typeof count !== 'number') return '';
    if (count === 1) return t('kyc.attemptRemainingOne');
    return t('kyc.attemptsRemaining', { count });
  };

  // Enforce Terms acceptance before allowing KYC
  useEffect(() => {
    if (authLoading || !currentUser) return;
    let cancelled = false;

    const checkTerms = async () => {
      setCheckingTerms(true);
      try {
        const { doc, getDocFromServer } = await import('firebase/firestore');
        const { db } = await import('../../firebase');
        const userDoc = await getDocFromServer(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists()) return;
        const data = userDoc.data() || {};
        const accepted = !!(data.termsAccepted || data.terms_accepted || data.termsAndConditionsAccepted);
        if (!cancelled && !accepted) {
          navigate('/terms', { replace: true, state: { next: '/kyc' } });
        }
      } catch (e) {
        // If this fails, don't block KYC hard; user existence checks / backend will still protect access.
        console.warn('Failed to check terms acceptance:', e);
      } finally {
        if (!cancelled) setCheckingTerms(false);
      }
    };

    checkTerms();
    return () => {
      cancelled = true;
    };
  }, [authLoading, currentUser, navigate]);

  // Continuously check if user exists in Firestore while on KYC page
  // Stop checking after KYC is completed to avoid redirecting away
  useEffect(() => {
    if (authLoading || !currentUser || kycCompleted) return;

    let cancelled = false;

    const checkUserExists = async () => {
      if (cancelled || kycCompleted) return;

      try {
        const { doc, getDocFromServer } = await import('firebase/firestore');
        const { db } = await import('../../firebase');
        const userDoc = await getDocFromServer(doc(db, 'users', currentUser.uid));

        if (!userDoc.exists()) {
          // User doesn't exist in Firestore - redirect to unauthorized
          // But only if we're not in the process of completing KYC
          if (!checking && !uploading) {
            console.warn('User document not found in Firestore, redirecting to unauthorized');
            navigate('/unauthorized', { replace: true });
          }
          return;
        }
      } catch (e) {
        console.error('Error checking user existence:', e);
        // On error, only redirect if not in the middle of KYC operations
        if (!checking && !uploading && !kycCompleted) {
          navigate('/unauthorized', { replace: true });
        }
        return;
      }

      // Check again after 5 seconds, but only if KYC is not completed
      if (!cancelled && !kycCompleted) {
        setTimeout(checkUserExists, 5000);
      }
    };

    // Initial check
    checkUserExists();

    return () => {
      cancelled = true;
    };
  }, [authLoading, currentUser, navigate, kycCompleted, checking, uploading]);

  // Create applicant on mount if needed
  useEffect(() => {
    if (authLoading || !currentUser) return;
    if (!USE_SUMSUB_KYC) return;

    // Allow KYC even if not approved yet - they can complete it while waiting for approval
    // The backend will handle the approval check when creating applicant
    const isAdmin = (userRoles || []).includes('admin');
    const isApprovedUser = isAdmin || !!isApproved;

    // Only show error if user has no roles (they need to select a role first)
    if (!userRoles || userRoles.length === 0) {
      setError(t('kyc.selectRoleFirst'));
      return;
    }

    // Don't block unapproved users - they can still complete KYC
    // if (!isApprovedUser) {
    //   setError(t('kyc.errors.notApproved'));
    //   return;
    // }

    if (applicantId) return; // Already have applicant ID

    const createApplicant = async () => {
      try {
        setCreatingApplicant(true);
        const token = await currentUser.getIdToken();
        const resp = await fetch(`${API_BASE_URL}/api/sumsub/create-applicant`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          // Handle 409 Conflict - applicant already exists
          if (resp.status === 409 && data.details?.description) {
            // Try to extract applicant ID from error message
            const description = data.details.description;
            const match = description.match(/already exists:\s*([a-zA-Z0-9]+)/);
            if (match && match[1]) {
              setApplicantId(match[1]);
              return; // Successfully extracted existing applicant ID
            }
          }
          throw new Error(data.error || 'Failed to create applicant');
        }
        if (data.applicantId) {
          setApplicantId(data.applicantId);
        }
      } catch (e) {
        console.error('Failed to create applicant:', e);
        setError(e?.message || t('kyc.errors.failedToCreateApplicant'));
      } finally {
        setCreatingApplicant(false);
      }
    };

    createApplicant();
  }, [authLoading, currentUser, isApproved, userRoles, applicantId, t]);

  const handleFileSelect = async (docType, file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError(t('kyc.errors.invalidFileType'));
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError(t('kyc.errors.fileTooLarge'));
      return;
    }

    setDocuments(prev => {
      const updated = prev.filter(doc => doc.type !== docType);
      return [...updated, { type: docType, file, uploaded: false }];
    });
    setError('');

    // Auto-upload the file after state is updated
    setTimeout(async () => {
      await handleUpload(docType, file);
    }, 100);
  };

  const handleUpload = async (docType, fileOverride = null) => {
    if (authLoading || !currentUser) {
      setError(t('common.loading'));
      return;
    }
    const doc = documents.find(d => d.type === docType);
    const fileToUpload = fileOverride || (doc?.file);
    if (!fileToUpload) {
      setError(t('kyc.errors.noFileSelected'));
      return;
    }

    setUploading(true);
    setError('');
    setStatusSnapshot(null);
    setWaitingForConfirmation(false);

    try {
      const token = await currentUser.getIdToken();
      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('card_type', 'israeli');

      if (USE_SUMSUB_KYC) {
        if (!applicantId) {
          setError(t('kyc.errors.noApplicantId'));
          return;
        }
        formData.append('applicantId', applicantId);
        formData.append('type', docType);
        formData.append('country', 'ISR'); // Default to Israel, can be made configurable

        const resp = await fetch(`${API_BASE_URL}/api/sumsub/upload-document`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data.error || t('kyc.errors.uploadFailed'));
        }

        // Mark document as uploaded
        setDocuments(prev => prev.map(d =>
          d.type === docType ? { ...d, uploaded: true } : d
        ));
      } else {
        if (isBlocked) {
          setError(t('kyc.manualReviewRequired'));
          return;
        }

        const resp = await fetch(`${API_BASE_URL}/api/kyc/submit-id-card`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await resp.json().catch(() => ({}));
        // Backend uses 423 when blocked; treat it as a normal response for UI.
        if (!resp.ok && resp.status !== 423) {
          throw new Error(data.error || t('kyc.errors.uploadFailed'));
        }

        setAttemptsRemaining(
          typeof data.attemptsRemaining === 'number' ? data.attemptsRemaining : null
        );
        setIsBlocked(!!data.blocked);
        setStatusSnapshot(data);

        if (data.kycCompleted) {
          // Mark document as uploaded (approved)
          setDocuments(prev => prev.map(d =>
            d.type === docType ? { ...d, uploaded: true } : d
          ));
          setKycCompleted(true);
          setTimeout(() => {
            window.location.reload();
          }, 1500);
          return;
        }

        // Check for duplicate ID number error
        if (data.code === 'kyc/duplicate-id-number' && data.error) {
          setError(data.error || t('kyc.errors.duplicateIdNumber'));
          setWaitingForConfirmation(false);
          return;
        }

        if (data.blocked) {
          setWaitingForConfirmation(true);
          setError('');
          return;
        }

        // Not approved yet, but still has tries left
        setError(t('kyc.ocrFailedTryAgain'));
      }
    } catch (e) {
      console.error('Upload error:', e);
      setError(e?.message || t('kyc.errors.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleCheckCompletion = async () => {
    if (!currentUser) return;
    if (!USE_SUMSUB_KYC) return;
    if (!applicantId) {
      setError(t('kyc.errors.noApplicantId'));
      return;
    }

    // Check if at least one document is uploaded
    const uploadedDocs = documents.filter(d => d.uploaded);
    if (uploadedDocs.length === 0) {
      setError(t('kyc.errors.noDocumentsUploaded'));
      return;
    }

    setChecking(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const resp = await fetch(`${API_BASE_URL}/api/sumsub/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ applicantId })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.error || t('kyc.errors.failedToCheck'));
      }
      setStatusSnapshot(data);
      if (data.kycCompleted) {
        setKycCompleted(true);
        setWaitingForConfirmation(false);
        // Force a page reload to ensure App.js picks up the updated KYC status from Firestore
        // This prevents the unauthorized redirect that happens when canAccessDashboard is false
        // Reload after a short delay to ensure Firestore write is complete
        setTimeout(() => {
          // Reload the page to refresh all auth state and KYC checks
          window.location.reload();
        }, 1500);
        return;
      }
      // If documents are uploaded but KYC is not completed yet, show waiting message
      const uploadedDocs = documents.filter(d => d.uploaded);
      if (uploadedDocs.length > 0) {
        setWaitingForConfirmation(true);
        setError('');
      } else {
        setWaitingForConfirmation(false);
        setError(t('kyc.errors.notCompletedYet'));
      }
    } catch (e) {
      console.error(e);
      setError(e?.message || t('kyc.errors.failedToCheck'));
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (e) {
      setError(t('kyc.errors.failedToLogout'));
    }
  };

  // Lightweight guard: if not signed in, send to login
  if (!authLoading && !currentUser) {
    navigate('/login', { replace: true });
    return null;
  }

  const getDocumentLabel = () => {
    return t('kyc.uploadIdCard');
  };

  // Upload icon SVG
  const UploadIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" fill="currentColor"/>
    </svg>
  );

  return (
    <div className={`kyc-page ${isRTL ? 'rtl' : ''}`}>
      <div className="kyc-background" style={{ backgroundImage: `url(${secondBackground})` }} />
      <div className="kyc-card">

        {isBusy && (
          <div className="kyc-loading" aria-live="polite" aria-busy="true">
            <div className="kyc-loading-spinner" />
            <p>{busyMessage}</p>
          </div>
        )}

        <div className="kyc-header">
          <h1>{t('kyc.title')}</h1>
        </div>

        {error && <div className="kyc-error">{error}</div>}

        {/* Waiting for KYC Confirmation Message */}
        {waitingForConfirmation && (
          <div className="kyc-waiting-confirmation">
            <p className="kyc-waiting-confirmation-text">
              {t('kyc.manualReviewRequired')}
            </p>
          </div>
        )}

        {/* Sumsub Status Information */}
        {USE_SUMSUB_KYC && !applicantId && creatingApplicant && (
          <div className="kyc-sumsub-status">
            <div className="kyc-sumsub-status-icon">⏳</div>
            <div className="kyc-sumsub-status-text">
              <strong>{t('kyc.creatingProfileTitle')}</strong>
              <p>{t('kyc.creatingProfileMessage')}</p>
            </div>
          </div>
        )}

        {(USE_SUMSUB_KYC ? !!applicantId : true) && (
          <>
            {/* Sumsub Info Box */}
            <div className="kyc-sumsub-info">
              <div className="kyc-sumsub-info-header">
                <span className="kyc-sumsub-info-icon">🔒</span>
                <strong>{USE_SUMSUB_KYC ? t('kyc.sumsubInfoTitle') : t('kyc.ocrInfoTitle')}</strong>
              </div>
              <p className="kyc-sumsub-info-text">
                {USE_SUMSUB_KYC ? t('kyc.sumsubInfoText') : t('kyc.ocrInfoText')}
              </p>
            </div>

            {/* Applicant ID Display */}
            {USE_SUMSUB_KYC && (
              <div className="kyc-applicant-id">
                <div className="kyc-applicant-id-label">{t('kyc.applicantId')}</div>
                <div className="kyc-applicant-id-value">{applicantId}</div>
              </div>
            )}

            {/* ID Card Upload Section */}
            <div className="kyc-upload-section">
              {(() => {
                const doc = documents.find(d => d.type === DOCUMENT_TYPE.value);
                const isUploaded = doc?.uploaded || false;

                return (
                  <div className="kyc-document-item">
                    <input
                      ref={el => fileInputRefs.current[DOCUMENT_TYPE.value] = el}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={(e) => handleFileSelect(DOCUMENT_TYPE.value, e.target.files[0])}
                      className="kyc-file-input"
                      id={`file-${DOCUMENT_TYPE.value}`}
                      disabled={isBusy || !currentUser || isUploaded || isBlocked}
                    />
                    <div className="kyc-document-header">
                      <div className="kyc-document-info">
                        <h3>{getDocumentLabel()}</h3>
                        {uploading && (
                          <div className="kyc-upload-status kyc-upload-pending">
                            <span className="kyc-inline-spinner" aria-hidden="true" />
                            {USE_SUMSUB_KYC ? t('kyc.uploadingToSumsub') : t('kyc.uploadingForVerification')}
                          </div>
                        )}
                        {!uploading && !isUploaded && (
                          <div className="kyc-upload-icon">
                            <UploadIcon />
                          </div>
                        )}
                        {isUploaded && (
                          <span className="kyc-upload-status kyc-upload-success">
                            ✓ {USE_SUMSUB_KYC ? t('kyc.uploadedToSumsub') : t('kyc.uploadedAndApproved')}
                          </span>
                        )}
                        {!USE_SUMSUB_KYC && attemptsRemaining !== null && !isUploaded && !isBlocked && (
                          <div className="kyc-upload-status">
                            {formatAttemptsRemaining(attemptsRemaining)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Status Display */}
            {statusSnapshot && (
              <div className="kyc-sumsub-status-display">
                <div className="kyc-sumsub-status-display-header">
                  <strong>{USE_SUMSUB_KYC ? t('kyc.sumsubStatusTitle') : t('kyc.ocrStatusTitle')}</strong>
                </div>
                <div className="kyc-sumsub-status-display-content">
                  {USE_SUMSUB_KYC ? (
                    <>
                      <div>
                        <strong>{t('kyc.status')}</strong>{' '}
                        {String(statusSnapshot?.reviewStatus?.reviewStatus || statusSnapshot?.reviewStatus || 'Pending')}
                      </div>
                      {statusSnapshot?.reviewResult?.reviewAnswer && (
                        <div>
                          <strong>{t('kyc.result')}</strong> {String(statusSnapshot.reviewResult.reviewAnswer)}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <strong>{t('kyc.status')}</strong> {String(statusSnapshot?.status || 'Pending')}
                      </div>
                      {typeof statusSnapshot?.attemptsRemaining === 'number' && (
                        <div>
                          <strong>{formatAttemptsRemaining(statusSnapshot.attemptsRemaining)}</strong>
                        </div>
                      )}
                      {statusSnapshot?.ocr?.confidence !== undefined && statusSnapshot?.ocr?.confidence !== null && (
                        <div>
                          <strong>{t('kyc.ocrConfidence')}</strong> {String(statusSnapshot.ocr.confidence)}
                        </div>
                      )}
                      {Array.isArray(statusSnapshot?.ocr?.failure_reasons) && statusSnapshot.ocr.failure_reasons.length > 0 && (
                        <div>
                          <strong>{t('kyc.ocrFailureReasons')}</strong>{' '}
                          {statusSnapshot.ocr.failure_reasons.join(', ')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {USE_SUMSUB_KYC && applicantId && (
          <button
            type="button"
            className="kyc-primary"
            onClick={handleCheckCompletion}
            disabled={checking || kycCompleted || documents.filter(d => d.uploaded).length === 0}
          >
            {checking ? t('kyc.checkingStatus') : t('kyc.checkStatus')}
          </button>
        )}
      </div>
    </div>
  );
}
